import './client-otp-modal.scss';

import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/components/Client/AutoCredit/shared/ui';
import AlertCircleIcon from '@/media/icons/alert-circle.svg?react';
import { formatPhoneNumber } from '@/services/formatter';
import { useAppSelector } from '@/store';

import useClientForm from './hooks/useClientForm';
import useOtpForm from './hooks/useOtpForm';
import type { ClientFormValues, TAgreementName } from './types/client-form';
import type { ModalOpenerType } from './types/general';
import type { OtpFormValues } from './types/otp-form';
import {
  BmgErrorModal,
  ClientForm,
  Loader,
  OtpForm,
  SecurityModal,
  Timer
} from './ui';

interface IProps {
  title: string;
  onClientSubmit: (values: ClientFormValues) => Promise<string>;
  onOtpSubmit: (values: OtpFormValues) => Promise<string>;
  onSMSRetry: (phone: string) => void;
  openedModal: ModalOpenerType;
  setOpenedModal: (modal: ModalOpenerType) => void;
  onClose: () => void;
  mainAgreements?: {
    policyLink: string;
    agreementLink: string;
  };
  additionalAgreements?: {
    name: TAgreementName;
    agreementLink: string;
    i18nKey: string;
  }[];
}

const ClientOtpModal: React.FC<IProps> = ({
  title,
  onClientSubmit,
  onOtpSubmit,
  openedModal,
  setOpenedModal,
  onClose,
  onSMSRetry,
  mainAgreements,
  additionalAgreements
}) => {
  const { t } = useTranslation();

  const { clientAdditionalInfo } = useAppSelector((state) => state.client);

  const {
    clientFormMethods,
    clientFormLoading,
    clientError,
    handleClientFormSubmit
  } = useClientForm({
    submitRequest: onClientSubmit,
    handleNext: () => {
      setOpenedModal('security-modal');
    }
  });
  const phone = useWatch({ control: clientFormMethods.control, name: 'phone' });

  const handleClose = () => {
    if (otpLoading || clientFormLoading) return;
    onClose();
    setOpenedModal('');
    resetOtpError();
  };

  const {
    otpFormMethods,
    handleOtpFormSubmit,
    otpLoading,
    otpError,
    resetOtpError
  } = useOtpForm({
    submitRequest: onOtpSubmit,
    onClose: handleClose
  });

  useEffect(() => {
    if (openedModal === '' || openedModal === 'bmg-error') {
      clientFormMethods.reset({
        iin: '',
        phone: formatPhoneNumber(
          clientAdditionalInfo?.initialValues?.phone_number
        ),
        policyAgreement: undefined,
        ...(additionalAgreements?.reduce(
          (acc, agreement) => {
            acc[agreement.name] = false;
            return acc;
          },
          {} as Record<TAgreementName, boolean>
        ) ?? {})
      });
    }
    if (openedModal === '' || openedModal === 'client-info-form') {
      otpFormMethods.reset({
        first: undefined,
        second: undefined,
        third: undefined,
        fourth: undefined
      });
    }
  }, [openedModal, clientAdditionalInfo]);

  useEffect(() => {
    if (openedModal === 'client-info-form') {
      clientFormMethods.clearErrors(['iin', 'phone']);
      // Фокусим после открытия модалки, иначе событие теряется
      setTimeout(() => clientFormMethods.setFocus('iin'), 200);
    } else if (openedModal === 'otp-form') {
      otpFormMethods.clearErrors(['first', 'second', 'third', 'fourth']);
      // Фокусим после открытия модалки, иначе событие теряется
      setTimeout(() => otpFormMethods.setFocus('first'), 200);
    }
  }, [openedModal]);

  return (
    <>
      <Modal
        className="client-otp-modal"
        title={title}
        open={openedModal === 'client-info-form'}
        onClose={handleClose}
      >
        {({ classes }) => (
          <ClientForm
            classes={classes}
            methods={clientFormMethods}
            clientFormLoading={clientFormLoading}
            clientError={clientError}
            handleClientFormSubmit={handleClientFormSubmit}
            mainAgreements={mainAgreements}
            additionalAgreements={additionalAgreements}
          />
        )}
      </Modal>

      <SecurityModal
        isOpened={openedModal === 'security-modal'}
        onClose={() => setOpenedModal('otp-form')}
      />

      <BmgErrorModal
        isOpened={openedModal === 'bmg-error'}
        onCancel={() => setOpenedModal('')}
        onRetry={() => setOpenedModal('client-info-form')}
      />

      <Modal
        className="client-otp-modal"
        title={t('otp-validation.enter-code')}
        open={openedModal === 'otp-form'}
        onClose={handleClose}
        destroyOnHidden
        mask={{ closable: false }}
      >
        <p className="client-otp-modal__subtitle">
          {t('otp-validation.code-sended')}
        </p>
        <p className="client-otp-modal__phone ym-disable-keys">
          {formatPhoneNumber(phone)}
        </p>

        <div className="client-otp-modal__form">
          <OtpForm
            resetOtpError={resetOtpError}
            methods={otpFormMethods}
            onSubmit={handleOtpFormSubmit}
          />
        </div>
        {otpError && (
          <p className="client-otp-modal__error" role="alert">
            <AlertCircleIcon />
            {otpError}
          </p>
        )}

        <div
          className={`client-otp-modal__otp-loading ${otpLoading ? 'client-otp-modal__otp-loading--active' : ''}`}
        >
          <Loader />
          <p>{t('otp-validation.wait')}...</p>
        </div>
        <div
          className={`client-otp-modal__timer ${!otpLoading ? 'client-otp-modal__timer--active' : ''}`}
        >
          <Timer
            timerStarted={openedModal === 'otp-form'}
            onRetry={() => onSMSRetry(phone || '')}
          />
        </div>
      </Modal>
    </>
  );
};

export default ClientOtpModal;
